// Lib
import { DependencyList, useCallback } from 'react';
import { createDispatchHook, createSelectorHook, Selector } from 'react-redux';

// Context
import { HotTableStoreContext } from './HotTableStoreProvider';

// Actions
import { getActiveTableThunk } from './hotTableStoreActions';
import {
    ActiveTableObject,
    HotTableStoreActions,
    HotTableStoreActiveTable,
    HotTableStoreState,
} from './hotTableStoreTypes';

// Only used within this file at them moment - expose elsewhere later if needed
const isActiveTableObject = (
    activeTable: HotTableStoreActiveTable | null | undefined,
): activeTable is ActiveTableObject => !!activeTable && 'elementId' in activeTable && 'session' in activeTable;

export const useHotTableDispatch = createDispatchHook<HotTableStoreState, HotTableStoreActions>(HotTableStoreContext);
const useHotTableSelector = createSelectorHook<HotTableStoreState, HotTableStoreActions>(HotTableStoreContext);

export type ActiveTableSelector<ReturnedType, OwnProps = undefined> = Selector<
    ActiveTableObject | undefined,
    ReturnedType,
    OwnProps
>;

const selectRuntimeActiveTable = (
    activeTable: HotTableStoreActiveTable | null | undefined,
): ActiveTableObject | undefined => (isActiveTableObject(activeTable) ? activeTable : undefined);

/**
 * This hook exposes the active table object.
 * Legacy active tables are intentionally hidden from this API.
 */
export const useActiveTableSelector = <ReturnedType, OwnProps = undefined>(
    selector: ActiveTableSelector<ReturnedType, OwnProps>,
): ReturnedType =>
    useHotTableSelector((state) => {
        const activeTable = selectRuntimeActiveTable(state.activeTable);

        // @ts-ignore We're passing activeTable as the third argument here, in case a createSelector is being used.
        //    This will force the selector to re-run even though the instance reference is stable.
        return selector(activeTable, undefined, activeTable);
    });

/**
 * This hook allows consumers to select values from the active HoT table, without needing
 * to worry about finding the instance within the store's structure.
 *
 * The use of "activeTable.instance" is so that we can ensure a re-render on active table changes.
 * The activeTable is a stable reference, so we need to wrap it in an object to ensure the reference changes.
 *
 * This hook allows selectors to ignore this detail.
 */
export const dangerouslyUseActiveTable = () =>
    useHotTableSelector((state) => selectRuntimeActiveTable(state.activeTable));

export const useActiveTableDataVersion = () =>
    useHotTableSelector((state) => selectRuntimeActiveTable(state.activeTable)?.tableDataVersion);

/**
 * Fires a thunk to get the active table object (instance + session, etc) from the HoT table store,
 * then provides the object to the callback function.
 */
export const useActiveTableCallback = <Args extends unknown[], ReturnValue>(
    func: (table: ActiveTableObject, ...args: Args) => ReturnValue,
    dependencies?: DependencyList,
): ((...args: Args) => ReturnValue) => {
    const dispatch = useHotTableDispatch();

    const returnFunc = (...args: Args) => {
        // @ts-ignore HotTable thunk typings - FIXME
        const activeTable = dispatch(getActiveTableThunk()) as unknown as HotTableStoreActiveTable | null;
        const activeTableObject = selectRuntimeActiveTable(activeTable);

        if (!activeTableObject) {
            throw new Error(`Failed to retrieve an active table object when firing an active table callback`);
        }

        return func(activeTableObject, ...args);
    };

    return dependencies ? useCallback(returnFunc, dependencies) : returnFunc;
};
